import { useState } from 'react';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import {
  Badge,
  Card,
  Heading,
  Inline,
  LinkButton,
  RichTextContent,
  Stack,
  Text,
  vars,
} from '@etg/wings';
import { CaretDown } from '@phosphor-icons/react';
import { AirportAvailability, ExpandableContent } from '@eti/components';
import { FAST_TRACK, getTranslatedTexts, getUpsellProps, LOUNGE } from '@eti/products';
import type { BoundNew, ProductSegmentAvailabilityText } from '@eti/schema-types';
import { TripType } from '@eti/schema-types';
import type { AvailableExtraProductsType } from '../v3/branded-fares/model';
import {
  availabilityTexts,
  getAvailabilityProductName,
  getAvailability,
  getProductdata,
  validateAvailabilityTranslation,
} from './utils';

const { id: FAST_TRACK_ID } = FAST_TRACK;
const { id: LOUNGE_ID } = LOUNGE;

const availabilityProductIds = [Number(FAST_TRACK_ID), Number(LOUNGE_ID)];

const contentWrapperStyles = css`
  padding: 16px 0;
`;

const headingStyles = css`
  justify-content: space-between;
`;

const iconStyles = (showMore: boolean) => css`
  color: ${vars.colors.links.default};
  flex-shrink: 0;
  transform: rotateX(${showMore ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;

const upSellStyles = css`
  display: flex;
  width: fit-content;
`;

export interface EtgFaresDrawerContentProps {
  availableExtraProducts: AvailableExtraProductsType[];
  bounds: BoundNew[];
  description: string | null;
  dimensions: string | null;
  id: number;
  name: string;
  piecesAndWeight: string | null;
  productTypeId: string;
  readMoreText?: string | null;
  termsUrl: string | null;
  tripType: TripType;
}

const EtgFaresDrawerContent = ({
  availableExtraProducts,
  bounds,
  description,
  dimensions,
  id,
  name,
  piecesAndWeight,
  productTypeId,
  readMoreText,
  termsUrl,
  tripType,
}: EtgFaresDrawerContentProps) => {
  const { t } = useTranslation();
  const readMoreButtonText = t('Products.ReadMore.Link.Label');
  const readLessButtonText = t('Products.ReadLess.Link.Label');
  const coverGeniusPolicyLinkText = t('coverGeniusPolicyLinkText');
  const openInNewTabText = t('ScreenReader.Link.OpensInNewTab.AriaLabel.Text');
  const priceLabel = t('Products.Price.Label.Total');

  const [accordionData, setAccordionData] = useState<ProductSegmentAvailabilityText | null>(null);
  const [showMore, setShowMore] = useState(false);
  const readMoreButtonLabel = showMore ? readLessButtonText : readMoreButtonText;
  const handleClick = () => {
    setShowMore((state) => !state);
  };
  const isAvailabilityProduct = availabilityProductIds.includes(Number(productTypeId));
  const isMultiStop = tripType !== TripType.OneWay && tripType !== TripType.Return;

  const toggleAccordion = (airport: ProductSegmentAvailabilityText) => {
    if (
      !airport ||
      (airport.boundIndex === accordionData?.boundIndex &&
        airport.segmentIndex === accordionData?.segmentIndex)
    ) {
      setAccordionData(null);
    } else {
      setAccordionData(airport);
    }
  };

  const termsLink = (
    <Inline>
      <a
        aria-label={`${coverGeniusPolicyLinkText} (${openInNewTabText})`}
        href={termsUrl ?? undefined}
        rel="noreferrer"
        target="_blank"
      >
        {coverGeniusPolicyLinkText}
      </a>
    </Inline>
  );

  const availabilityList = isAvailabilityProduct
    ? getAvailability(availableExtraProducts, bounds, productTypeId)
    : [];

  const availabilityProductName = getAvailabilityProductName(productTypeId);
  const translation = getTranslatedTexts(availabilityTexts[availabilityProductName], t);
  const hasAllTranslation = validateAvailabilityTranslation(
    productTypeId,
    translation?.airportAvailability,
  );

  const shouldDisplayAvailability = availabilityList.length > 0 && hasAllTranslation;

  const { upSellObject, shouldDisplayUpsell = false } = shouldDisplayAvailability
    ? getUpsellProps(translation.upSell, {
        shouldDisplayAvailability,
        availabilityList,
        tripType,
      })
    : {};

  return (
    <Card>
      <Inline alignY="center" className={headingStyles}>
        <Heading data-testid="content-title" level={3}>
          <bdi>
            {name}
            {piecesAndWeight && ` (${piecesAndWeight})`}
          </bdi>
        </Heading>
        {shouldDisplayAvailability && upSellObject?.content && (
          <Badge
            className={upSellStyles}
            data-testid="partial-availability-pill-information"
            variant="information"
          >
            {upSellObject.content}
          </Badge>
        )}
      </Inline>
      <RichTextContent className={contentWrapperStyles}>{description}</RichTextContent>
      <Stack>
        {piecesAndWeight && <Text as="bdi">{piecesAndWeight}</Text>}
        {dimensions && <Text as="bdi">{dimensions}</Text>}
        {termsUrl && !readMoreText && termsLink}
        {readMoreText && !piecesAndWeight && (
          <>
            <Inline>
              <LinkButton
                aria-controls={`read-more-${id}`}
                aria-expanded={showMore}
                aria-label={`${readMoreButtonLabel} ${name} ${upSellObject?.content ?? ''}`}
                data-testid="readMore-button"
                onClick={handleClick}
              >
                <Inline noWrap spacing={4}>
                  {readMoreButtonLabel}
                  <CaretDown
                    aria-hidden
                    className={iconStyles(showMore)}
                    size={20}
                    weight="light"
                  />
                </Inline>
              </LinkButton>
            </Inline>
            <ExpandableContent isExpanded={showMore}>
              <Stack id={`read-more-${id}`}>
                {shouldDisplayAvailability && (
                  <AirportAvailability
                    accordionData={accordionData}
                    isMultiStop={isMultiStop}
                    productData={getProductdata(
                      availabilityList,
                      availableExtraProducts,
                      name,
                      priceLabel,
                      productTypeId,
                      translation,
                      shouldDisplayUpsell,
                      upSellObject,
                    )}
                    testid="etgFares-yst"
                    toggleAccordion={toggleAccordion}
                  />
                )}
                <RichTextContent>{readMoreText}</RichTextContent>
                {termsUrl && termsLink}
              </Stack>
            </ExpandableContent>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default EtgFaresDrawerContent;
